<template>
    <page-content
        :loading="isLoading"
        :error="error"
    >
        <v-container
            v-if="productReturn"
            grid-list-lg
        >
            <v-layout
                row
                wrap
            >
                <v-flex
                    xs12
                    md6
                >
                    <h1 class="display-1 mb-1">
                        Product Return
                    </h1>
                </v-flex>
                <v-spacer />
                <v-flex
                    xs12
                    md6
                    class="text-md-right"
                >
                    <upload-photos
                        :return-id="productReturn.uuid"
                        @refresh-data="onRefreshData"
                    />
                </v-flex>
            </v-layout>
            <v-layout
                row
                wrap
            >
                <v-flex
                    xs12
                    sm8
                >
                    <v-layout
                        row
                        wrap
                        mb-2
                    >
                        <v-flex
                            xs12
                            md6
                            offset-md6
                        >
                            <component
                                :is="actionsComponent"
                                :return-id="productReturn.uuid"
                                @refresh-data="onRefreshData"
                            />
                        </v-flex>
                    </v-layout>
                    <v-card
                        v-if="productReturn"
                        class="mb-2"
                    >
                        <v-card-title>
                            <return-info
                                :product-return="productReturn"
                                office-use
                            />
                        </v-card-title>
                    </v-card>
                </v-flex>
                <v-flex
                    xs12
                    sm4
                >
                    <invoice-card
                        v-if="invoiceCard"
                        :invoice="invoiceCard"
                    />
                </v-flex>
            </v-layout>
            <v-layout>
                <v-flex
                    xs12
                    sm8
                >
                    <h2 class="headline mb-1">
                        Comments
                    </h2>
                    <comments
                        :comments="comments"
                        :return-id="productReturn.uuid"
                        @refresh-data="onRefreshData"
                        :disabled="disableComments"
                    />
                </v-flex>
            </v-layout>
            <v-layout v-if="showDeleteBtn">
                <v-flex
                    xs12
                    sm8
                >
                    <!-- Delete Button -->
                    <v-btn
                        class="my-0"
                        color="error"
                        block
                        @click.stop="deleteDialog = true"
                    >
                        <v-icon
                            left
                            size="20"
                        >
                            fa-check
                        </v-icon>
                        Delete Return
                    </v-btn>
                </v-flex>
                <!-- Delete Dialog -->
                <v-dialog
                    v-model="deleteDialog"
                    max-width="600"
                >
                    <v-card>
                        <v-card-title>
                            <h3 class="headline">
                                Delete Return
                            </h3>
                        </v-card-title>
                        <v-card-text>
                            <p>Are you sure you want to delete this return?</p>
                            <v-form
                                @submit.prevent="onDelete"
                            />
                        </v-card-text>
                        <v-card-actions>
                            <v-spacer />
                            <v-btn
                                flat
                                @click="deleteDialog = false"
                            >
                                Cancel
                            </v-btn>
                            <v-btn
                                color="primary"
                                @click="onDelete"
                            >
                                Confirm
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-layout>
        </v-container>
    </page-content>
</template>

<script>
import axios from 'axios'
import ReturnInfo from '../../components/ReturnInfo'
import InvoiceCard from '../../components/InvoiceCard'
import UploadPhotos from '../../components/UploadPhotos'
import Comments from '../../components/Comments'
import PendingCustomerServiceActions from './Actions/PendingCustomerService'
import PendingFreightGroupActions from './Actions/PendingFreightGroup'
import OpenActions from './Actions/Open'
import InTransitActions from './Actions/InTransit'
import ProductReceivedActions from './Actions/ProductReceived'
import { requestErrorMessage } from '../../utils/constants/errors'
import UserBus from '../../utils/buses/userBus'
import { OPEN, PENDING_CUSTOMER_SERVICE, PENDING_FREIGHT_GROUP, IN_TRANSIT, PRODUCT_RECEIVED, CLOSED } from '../../utils/constants/statuses'
import SnackBus from '../../utils/buses/snackBus'

export default {
    components: {
        ReturnInfo,
        InvoiceCard,
        Comments,
        PendingCustomerServiceActions,
        PendingFreightGroupActions,
        InTransitActions,
        ProductReceivedActions,
        OpenActions,
        UploadPhotos
    },
    data () {
        return {
            isLoading: true,
            error: null,
            deleteDialog: false,
            showDeleteBtn: false,
            productReturn: null,
            invoiceCard: null,
            comments: [],
            internalFiles: []
        }
    },
    computed: {
        /**
         * determines what actions should be shown based on status and user role
         */
        actionsComponent: function () {
            if (!this.productReturn) return null

            let component = null
            switch (this.productReturn.status) {
            case OPEN:
                if (UserBus.userIs.salesRep()) {
                    component = OpenActions
                }
                break
            case PENDING_CUSTOMER_SERVICE:
                if (UserBus.userIs.customerService()) {
                    component = PendingCustomerServiceActions
                }
                break
            case PENDING_FREIGHT_GROUP:
                if (UserBus.userIs.freightGroup()) {
                    component = PendingFreightGroupActions
                }
                break
            case IN_TRANSIT:
                if (UserBus.userIs.freightGroup()) {
                    component = InTransitActions
                }
                break
            case PRODUCT_RECEIVED:
                if (UserBus.userIs.customerService()) {
                    component = ProductReceivedActions
                }
                break
            case CLOSED:
                component = null
                break
            default:
                component = null
            }
            return component
        },
        /**
         * determine if comments should be disabled
         */
        disableComments: function () {
            return this.productReturn && !UserBus.userIs.prpAdmin() && this.productReturn.status === CLOSED
        }
    },
    created () {
        this.isLoading = true
        if (UserBus.userIs.prpAdmin()) {
            this.showDeleteBtn = true
        }

        this.getData()
            .catch(err => {
                console.log('Read Return Error', err)
                this.error = requestErrorMessage
            })
            .finally(() => {
                this.isLoading = false
            })
    },
    methods: {
        getData () {
            return axios.get('/api/returns/' + this.$route.params.id)
                .then(res => {
                    this.productReturn = res.data

                    const metadata = res.data.metadata || {}
                    this.invoiceCard = {
                        invoiceNumber: res.data.invoiceNumber,
                        partnerNumber: metadata.partnerNumber,
                        amountPaid: metadata.amountPaid,
                        salesRep: metadata.salesRep,
                        projectName: metadata.projectName
                    }
                    this.comments = res.data.comments
                })
        },
        onRefreshData () {
            this.getData()
                .catch(err => {
                    console.log('Refresh Return Error', err)
                    SnackBus.showMessage('Error refreshing data', 'error')
                })
        },
        onDelete () {
            axios.delete('/api/returns/' + this.$route.params.id)
                .then(res => {
                    // redirect back to dashboard
                    this.$router.push({ path: '/dashboard' })
                })
                .catch(err => {
                    console.log('Return Deletion Error', err)
                    SnackBus.showMessage(requestErrorMessage, 'error')
                })
                .finally(() => {
                })
        }
    }
}
</script>

<style lang="scss" scoped>

</style>
